import { Fragment } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { SectionHeader } from "../SectionHeader";
import RAGBadge from "../RAGBadge";
import ThemeChip from "../ThemeChip";
import { fmtCr } from "../../utils/format";

export function GranteeTable({
  granteeList, page, setPage,
  expandedGrantees, expandLoading, toggleGranteeExpand,
}) {
  return (
    <SectionHeader title={`Grantee List (${granteeList.total})`} subtitle="Click any row to expand grants">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="grantee-list-table">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left text-xs font-medium text-slate-500 uppercase py-2 pr-4">#</th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase py-2">Grantee</th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase py-2">Theme</th>
              <th className="text-right text-xs font-medium text-slate-500 uppercase py-2">Grants</th>
              <th className="text-right text-xs font-medium text-slate-500 uppercase py-2">Sanctioned</th>
              <th className="text-right text-xs font-medium text-slate-500 uppercase py-2">Disbursed</th>
              <th className="text-right text-xs font-medium text-slate-500 uppercase py-2">Utilised</th>
            </tr>
          </thead>
          <tbody>
            {granteeList.data.map((r, i) => {
              const isExpanded = expandedGrantees[r.grantee] !== undefined;
              const isLoading = expandLoading[r.grantee];
              const grantDetails = expandedGrantees[r.grantee] || [];
              return (
                <Fragment key={`grantee-${r.grantee}`}>
                  <tr
                    className={`border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${isExpanded ? "bg-red-50/20" : ""}`}
                    onClick={() => toggleGranteeExpand(r.grantee, r.grants, r.theme)}
                    data-testid={`grantee-row-${i}`}
                  >
                    <td className="py-2.5 text-xs text-slate-600 pr-4">{(page - 1) * 20 + i + 1}</td>
                    <td className="py-2.5 font-medium text-slate-900 text-sm">
                      <div className="flex items-center gap-1.5">
                        <ChevronDown size={13} className={`text-slate-600 flex-shrink-0 transition-transform ${isExpanded ? "rotate-180" : ""} ${isLoading ? "animate-spin" : ""}`} />
                        <span>{r.grantee}</span>
                      </div>
                    </td>
                    <td className="py-2.5"><ThemeChip theme={r.theme} /></td>
                    <td className="py-2.5 text-right text-xs">{r.grants}</td>
                    <td className="py-2.5 text-right text-xs text-slate-600">{r.allocation_cr ? fmtCr(r.allocation_cr) : "–"}</td>
                    <td className="py-2.5 text-right text-xs font-semibold text-[#B71C1C]">{fmtCr(r.disbursed_cr)}</td>
                    <td className="py-2.5 text-right text-xs text-slate-600">{r.utilised_cr ? fmtCr(r.utilised_cr) : "–"}</td>
                  </tr>
                  {isExpanded && grantDetails.length > 0 && (
                    <tr key={`subhdr-${i}`} className="bg-slate-100/70">
                      <td className="py-1.5 pl-8 text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Grant ID</td>
                      <td className="py-1.5 text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Project Title</td>
                      <td className="py-1.5 text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Grant Type</td>
                      <td className="py-1.5 text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Programme Officer</td>
                      <td className="py-1.5 text-right text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Sanctioned</td>
                      <td className="py-1.5 text-right text-[10px] text-slate-500 font-semibold uppercase tracking-wide whitespace-nowrap">Disbursed (Till Date)</td>
                      <td className="py-1.5 text-right text-[10px] text-slate-500 font-semibold uppercase tracking-wide whitespace-nowrap">Disbursed (YTD)</td>
                      <td className="py-1.5 text-right text-[10px] text-slate-500 font-semibold uppercase tracking-wide">KPI %</td>
                    </tr>
                  )}
                  {isExpanded && grantDetails.map((g, gi) => (
                    <tr key={`detail-${i}-${gi}`} className="bg-slate-50/80 border-b border-slate-100" data-testid={`grant-detail-${i}-${gi}`}>
                      <td className="py-2 pl-8 text-xs font-mono whitespace-nowrap">
                        <Link
                          to={`/grant-overview?grant=G-${g.grant_id}`}
                          className="text-[#B71C1C] hover:text-red-800 underline decoration-dotted underline-offset-2"
                          data-testid={`grant-id-link-${g.grant_id}`}
                          onClick={(e) => e.stopPropagation()}
                          title="Open grant detail"
                        >
                          {g.grant_id}
                        </Link>
                      </td>
                      <td className="py-2 text-xs text-slate-700 max-w-[200px]">
                        <div className="truncate font-medium" title={g.project_title}>{g.project_title}</div>
                        <div className="text-slate-600 text-[10px] mt-0.5">{g.fy} · {g.status}</div>
                      </td>
                      <td className="py-2 text-xs">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700 whitespace-nowrap">
                          {g.grant_type || "Grants"}
                        </span>
                      </td>
                      <td className="py-2 text-xs text-slate-700 whitespace-nowrap">{g.po}</td>
                      <td className="py-2 text-right text-xs text-slate-600">{g.allocation_cr ? fmtCr(g.allocation_cr) : "–"}</td>
                      <td className="py-2 text-right text-xs font-semibold text-[#B71C1C]">{fmtCr(g.disbursed_cr)}</td>
                      <td className="py-2 text-right text-xs font-semibold text-emerald-700">{g.disbursed_ytd_cr != null ? fmtCr(g.disbursed_ytd_cr) : "–"}</td>
                      <td className="py-2 text-right text-xs">
                        {g.kpi_pct != null ? (
                          <span className="font-medium text-slate-700">{g.kpi_pct}%
                            <span className="text-[10px] text-slate-600 ml-1">({g.kpi_count})</span>
                          </span>
                        ) : <span className="text-slate-500">—</span>}
                      </td>
                    </tr>
                  ))}
                  {isExpanded && grantDetails.length > 0 && (
                    <tr key={`subfoot-${i}`} className="bg-white border-b-2 border-slate-200">
                      <td colSpan={8} className="py-1 pl-8 text-[10px] text-slate-600 italic">
                        {grantDetails.length} grants shown · matching current filters · KPI % = avg MEAS indicator achievement
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {granteeList.total > 20 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
            <span className="text-xs text-slate-500">
              {(page - 1) * 20 + 1}–{Math.min(page * 20, granteeList.total)} of {granteeList.total}
            </span>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="text-xs px-3 py-1.5 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40">Prev</button>
              <button onClick={() => setPage(p => p + 1)} disabled={page * 20 >= granteeList.total}
                className="text-xs px-3 py-1.5 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>
    </SectionHeader>
  );
}
